<template>
    <IonHeader
        class="ion-no-border"
        data-cy="filters-select-details-modal-header"
    >
        <div class="w-full font-medium text-md border-b border-b-transparent transition-colors duration-300 py-2">
            <div class="w-full flex items-center justify-between px-3 overflow-hidden">
                <DButton
                    data-cy="filters-select-back"
                    theme="transparent"
                    class="mr-3 -ml-1 w-[38px] h-[38px] flex items-center justify-center rounded-full"
                    @click="emit('close')"
                >
                    <PhIcon
                        v-if="isIos"
                        icon="caret-left"
                        weight="light"
                        :size="24"
                    />
                    <PhIcon
                        v-else
                        icon="arrow-left"
                        weight="light"
                        :size="24"
                    />
                </DButton>

                <span
                    class="flex-1 truncate text-gray-900 font-medium"
                    :class="isIos ? 'text-center ml-[10px]' : 'text-left'"
                >
                    {{ filter.title }}
                </span>

                <div class="w-[60px] shrink flex justify-end">
                    <DButton
                        theme="text-link"
                        :link-underline="false"
                        :disabled="!canClear"
                        @click="clear"
                    >
                        Clear
                    </DButton>
                </div>
            </div>
        </div>
    </IonHeader>
    <IonContent data-cy="filters-select-details-modal-content">
        <div class="pt-4">
            <div
                class="py-3 pl-4 pr-4 flex items-center justify-between cursor-pointer"
                @click="selectAll(!allSelected)"
            >
                <div class="content-md-ux-medium">
                    Select all
                </div>
                <!-- The entire row is clickable so prevent potential issues with nested click handlers by disabling pointer events, it is still keyboard navigable so keep the event listener -->
                <div class="scale-[1.3] pointer-events-none">
                    <DCheckbox
                        :model-value="allSelected"
                        @update:model-value="selectAll($event)"
                    />
                </div>
            </div>
            <div
                v-for="option in filter.options"
                :key="option.value"
                class="py-3 pl-4 pr-4 flex items-center justify-between cursor-pointer"
                @click="selectOption(option)"
            >
                <div>
                    {{ option.title }}
                </div>
                <!-- The entire row is clickable so prevent potential issues with nested click handlers by disabling pointer events, it is still keyboard navigable so keep the event listener -->
                <div class="scale-[1.3] pointer-events-none">
                    <DCheckbox
                        :model-value="optionStatuses[option.value]"
                        @update:model-value="selectOption(option)"
                    />
                </div>
            </div>
        </div>
    </IonContent>
</template>
<script setup>
    import { DButton, DCheckbox } from '@digistorm/spark'
    import { cloneDeep, reduce } from 'lodash'

    const emit = defineEmits(['update:modelValue', 'close'])

    const props = defineProps({
        filter: Object,
        modelValue: Object,
    })

    const deviceStore = useDeviceStore()

    const { isIos } = storeToRefs(deviceStore)

    const currentValue = ref(cloneDeep(props.modelValue ?? {}))

    const optionStatuses = computed(() => {
        return reduce(currentValue.value.selected ?? [], (statuses, option) => {
            statuses[option] = true
            return statuses
        }, {})
    })

    const canClear = computed(() => {
        return currentValue.value?.selected?.length > 0
    })

    const allSelected = computed(() => {
        return props.filter.options.every((option) => optionStatuses.value[option.value])
    })

    const selectAll = (selectAll) => {
        currentValue.value ??= {}
        currentValue.value.selected = selectAll
            ? props.filter.options.map((option) => option.value)
            : []
    }

    const selectOption = (option) => {
        currentValue.value ??= {}
        currentValue.value.selected ??= []

        const isSelected = optionStatuses.value[option.value] ?? false
        currentValue.value.selected = isSelected
            ? currentValue.value.selected.filter((value) => value !== option.value)
            : [...currentValue.value.selected, option.value]
    }

    const clear = () => {
        currentValue.value ??= {}
        currentValue.value.selected = []
    }

    watch(() => currentValue.value.selected, (selected) => {
        emit(
            'update:modelValue',
            selected?.length
                ? currentValue.value
                : null,
        )
    })
</script>
